import React, { type JSX } from 'react';
import { Box, Typography, IconButton, Stack } from '@mui/material';
import { CustomTransComponent } from 'components/CustomTransComponent';
import { LoadListsHierarchy } from './LoadListsHierarchy';
import type { SelectedLoad } from './AddLoads';
import { InfoListItem } from '@brightlayer-ui/react-components';
import CloseIcon from '@mui/icons-material/Close';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';
import { GroupPath } from 'components/GroupPath';
import { useNewCustomTranslations } from 'hooks';

type Props = {
    selectedLoadIds: SelectedLoad[];
    selectedDeviceId: string;
    setSelectedAddLoads: (loads: SelectedLoad[]) => void;
    existingLoadIds?: string[];
};

export const AddSingleDeviceLoads = ({
    selectedLoadIds,
    selectedDeviceId,
    setSelectedAddLoads,
    existingLoadIds = [],
}: Props): JSX.Element => {
    const { data: selectedDeviceData } = useGetDeviceByIdQuery(selectedDeviceId, { skip: !selectedDeviceId });
    const loadNumberStyle = {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#727E84',
        color: '#fff',
        width: '18px',
        height: '18px',
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    };
    useNewCustomTranslations('WIDGETS');
    useNewCustomTranslations('WIDGET_MANAGEMENT');
    const getLoadId = (load: any): string => load?.id || load?.loadId || '';
    const getDisplayLoadNumber = (load: SelectedLoad): string => {
        if (load?.loadNumber === undefined || load?.loadNumber === null || load?.loadNumber === '') {
            return '';
        }

        const parsedLoadNumber = Number(load.loadNumber);

        return Number.isNaN(parsedLoadNumber) ? String(load.loadNumber) : String(parsedLoadNumber + 1);
    };

    const handleRemoveLoad = (loadId: string): void => {
        setSelectedAddLoads(selectedLoadIds.filter((item) => getLoadId(item) !== loadId));
    };

    return (
        <Box p={2} display="flex" flexDirection="column">
            <Box display="flex" justifyContent={'space-between'}>
                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2' }}>
                    <Box className="max-height-570 bg-white">
                        <LoadListsHierarchy
                            selectedDevice={{
                                id: selectedDeviceId,
                                name: selectedDeviceData?.data?.name,
                                groupPath: selectedDeviceData?.data?.groupPath,
                            }}
                            createPayload={selectedLoadIds}
                            setCreatePayload={setSelectedAddLoads}
                            existingLoadIds={existingLoadIds}
                        />
                    </Box>
                </Box>

                <Box p={2} sx={{ width: '49%', backgroundColor: '#f2f2f2' }}>
                    <Typography fontSize={16} fontWeight={600} color="#424e54" variant="h6" mb={1}>
                        <CustomTransComponent translationKey={'WIDGET_MANAGEMENT:SELECT_LOADS'} />
                    </Typography>
                    <Typography fontSize={16} fontWeight={400} color="#424e54" variant="h6" mb={2}>
                        <CustomTransComponent translationKey={'WIDGETS:LOADS_GROUPS.SELECT_SIXTEEN_LOADS'} />
                    </Typography>
                    <Typography variant="h6" mb={1} fontSize={12} letterSpacing={2} fontWeight={600} color="#424e54">
                        <CustomTransComponent translationKey={'COMMON:SELECTED_LOADS'} /> ({selectedLoadIds.length ?? 0}
                        /16)
                    </Typography>

                    <Stack spacing={1} className="bg-white">
                        {selectedLoadIds.map((item) => (
                            <InfoListItem
                                key={item?.id}
                                rightComponent={
                                    <IconButton onClick={() => handleRemoveLoad(getLoadId(item))}>
                                        <CloseIcon />
                                    </IconButton>
                                }
                                title=""
                                subtitle={[
                                    <>
                                        <Stack flexDirection={'row'} alignItems={'center'}>
                                            <Box sx={loadNumberStyle}>{getDisplayLoadNumber(item)}</Box>
                                            <Typography fontSize={14} fontWeight={400} color="#424e54" pl={1}>
                                                {item?.loadName ?? item?.name}
                                            </Typography>
                                        </Stack>
                                    </>,
                                ]}
                                info={[
                                    <GroupPath
                                        key={item?.id}
                                        pathName={item?.groupPath ?? selectedDeviceData?.data?.groupPath}
                                        deviceName={item?.deviceName ?? selectedDeviceData?.data?.name}
                                        width="250px"
                                    />,
                                ]}
                                sx={{
                                    '& .MuiList-root': {
                                        paddingBottom: 0,
                                    },
                                    '& .MuiListItem-root': {
                                        marginTop: 0,
                                    },
                                }}
                                dense
                                divider="full"
                                hidePadding
                            />
                        ))}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};
